// Riel de pasos de un asistente.
//
// No había ninguno en el repo: cada cajón resolvía su navegación como podía. Este
// nace genérico porque un paso a paso que no se ve es un formulario largo con
// botones, y el usuario no sabe cuánto le queda.
//
// Dos reglas de uso que son de diseño, no de implementación:
//
//   · hacia ATRÁS siempre se puede, porque revisar lo ya hecho no rompe nada;
//   · hacia ADELANTE sólo a un paso ya alcanzado. Dejar saltar al final con la
//     cabecera a medias produce un error que el usuario no sabe de dónde viene.
import type { ReactNode } from 'react'

export interface Paso {
  id: string
  titulo: string
  /** Una palabra sobre lo que ya se decidió ahí. Se pinta bajo el título del
   *  paso ya resuelto: es lo que permite comprobar de un vistazo si uno se
   *  equivocó dos pasos atrás sin tener que volver. */
  resumen?: ReactNode
}

interface Props {
  dark: boolean
  pasos: Paso[]
  /** Índice del paso que se está viendo. */
  actual: number
  /** Hasta dónde ha llegado el usuario. Marca el tope de la navegación. */
  alcanzado: number
  onIr: (indice: number) => void
}

export function RielPasos({ dark, pasos, actual, alcanzado, onIr }: Props) {
  const linea = dark ? 'bg-white/10' : 'bg-slate-200'
  const lineaHecha = 'bg-sky-500'

  return (
    <nav
      aria-label="Pasos"
      className={`flex items-start gap-0 px-6 py-3.5 border-b shrink-0 ${
        dark ? 'border-white/[0.07]' : 'border-slate-200'
      }`}
    >
      {pasos.map((paso, i) => {
        const hecho = i < alcanzado
        const esActual = i === actual
        const accesible = i <= alcanzado
        const circulo = esActual
          ? 'bg-sky-500 text-white ring-4 ring-sky-500/20'
          : hecho
            ? 'bg-sky-500/15 text-sky-500'
            : dark
              ? 'bg-white/[0.06] text-slate-600'
              : 'bg-slate-100 text-slate-400'

        return (
          <div key={paso.id} className="flex items-start flex-1 min-w-0">
            <button
              type="button"
              disabled={!accesible}
              onClick={() => accesible && onIr(i)}
              aria-current={esActual ? 'step' : undefined}
              className={`flex flex-col items-center gap-1.5 shrink-0 group ${
                accesible ? 'cursor-pointer' : 'cursor-default'
              }`}
            >
              <span
                className={`w-6 h-6 rounded-full grid place-items-center text-[10px] font-semibold tabular-nums transition-all ${circulo}`}
              >
                {hecho ? (
                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                ) : (
                  i + 1
                )}
              </span>
              <span className="flex flex-col items-center gap-0.5 max-w-[11rem]">
                <span
                  className={`text-[11px] leading-none font-medium transition-colors ${
                    esActual
                      ? dark ? 'text-slate-100' : 'text-slate-900'
                      : accesible
                        ? dark ? 'text-slate-400 group-hover:text-slate-200' : 'text-slate-500 group-hover:text-slate-700'
                        : dark ? 'text-slate-600' : 'text-slate-400'
                  }`}
                >
                  {paso.titulo}
                </span>
                {paso.resumen && (
                  <span
                    className={`text-[10px] leading-tight text-center truncate max-w-full ${
                      dark ? 'text-slate-600' : 'text-slate-400'
                    }`}
                  >
                    {paso.resumen}
                  </span>
                )}
              </span>
            </button>

            {i < pasos.length - 1 && (
              <span
                aria-hidden
                className={`flex-1 h-px mt-3 mx-2 rounded transition-colors ${
                  hecho ? lineaHecha : linea
                }`}
              />
            )}
          </div>
        )
      })}
    </nav>
  )
}
